// NPC-doc inheritance — the pure flatten for `base` variant NPCs, the twin of
// resolveModelDoc in src/model/variants.ts. THREE/DOM-free (imports only the
// NpcDoc *type* + the shared deepMerge), so the loader (src/content/load.ts) and
// the validator (src/content/validate.ts) agree on the merged doc byte-for-byte.
//
// Single-level chains only (a base may not itself have a base) — validation
// reports a dangling/chained base first, we assert here as a backstop.
//
// Merge rules (child wins; a field the child omits → inherited from the base):
//   • scalars + arrays (name, model, scale, dialogue, loot, faction, radius,
//     height, solid, colliderR/H, assistRadius, nameplate) → REPLACE
//   • combat → DEEP-MERGE, so a variant can override just hp / attackDamage and
//     keep the rest of the base's block
//   • territory / talk / flee → REPLACE (whole object — partials would be
//     incomplete, e.g. territory needs both radius + leash)
//
// Placement is NOT part of the doc (plan 21): spawns live in the map docs'
// `npcs` arrays and reference a kind by id — a variant is spawned by naming it
// there, so there is nothing placement-shaped to merge here.

import type { NpcDoc } from "../content/schemas.ts";
import { deepMerge } from "../model/variants.ts";

/** Flatten a (possibly variant) NPC doc against its base. `lookup` resolves a
 *  base id to its doc. Returns the doc unchanged when it has no `base`. Throws on
 *  a dangling or chained base — validate.ts surfaces both before this can fire. */
export function resolveNpcDoc(
  child: NpcDoc,
  lookup: (id: string) => NpcDoc | undefined,
): NpcDoc {
  if (!child.base) return child;
  const base = lookup(child.base);
  if (!base) throw new Error(`npc "${child.id}": unknown base "${child.base}"`);
  if (base.base) throw new Error(`npc "${child.id}": base "${base.id}" is itself a variant (single-level chains only)`);

  const combat =
    child.combat && base.combat
      ? (deepMerge(
          base.combat as unknown as Record<string, unknown>,
          child.combat as unknown as Record<string, unknown>,
        ) as unknown as NpcDoc["combat"])
      : (child.combat ?? base.combat);

  // Start from the base, overlay every DEFINED child field (replace), then fix up
  // the fields with special rules.
  const merged: NpcDoc = { ...base };
  for (const [k, v] of Object.entries(child)) {
    if (v !== undefined) (merged as Record<string, unknown>)[k] = v;
  }
  merged.id = child.id;
  merged.combat = combat;
  merged.base = undefined; // fully flattened
  return merged;
}
